import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, CheckCircle, Clock, AlertTriangle, Download,
  Activity, Shield, Link, Search, Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';

/**
 * LivePortalTab Component
 * 
 * Provides interface for LIVE PerformCare portal integration
 * using web automation (Playwright)
 */
const LivePortalTab = ({ token, apiCall, clients = [], toast, showSections }) => {
  const [liveStatus, setLiveStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncJobId, setSyncJobId] = useState(null);
  const [syncJobStatus, setSyncJobStatus] = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [verifyingEligibility, setVerifyingEligibility] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [searchByName, setSearchByName] = useState(false);  // New state for search method

  const loadLiveStatus = useCallback(async () => {
    try {
      const response = await apiCall('/performcare/live/status', 'GET', null, token);
      setLiveStatus(response?.data?.data || response?.data || response);
    } catch (error) {
      console.error('Error loading live status:', error);
    }
  }, [apiCall, token]);

  // Load live status
  useEffect(() => {
    loadLiveStatus();
  }, [loadLiveStatus]);

  const testConnection = async () => {
    setTesting(true);
    try {
      const response = await apiCall('/performcare/live/test-connection', 'POST', {}, token);
      const data = response?.data?.data || response?.data || response;
      
      if (data.authenticated) {
        toast?.success('Successfully connected to PerformCare portal!');
      } else {
        toast?.error(`Connection failed: ${data.error || 'Unknown error'}`);
      }
      
      await loadLiveStatus();
    } catch (error) {
      toast?.error('Failed to test connection');
      console.error(error);
    } finally {
      setTesting(false);
    }
  };

  const verifyClientEligibility = async () => {
    if (!selectedClient) {
      toast?.error('Please select a client');
      return;
    }
    
    setVerifyingEligibility(true);
    setEligibilityResult(null);
    
    try {
      const response = await apiCall('/performcare/live/eligibility/verify', 'POST', {
        client_ids: [selectedClient],
        search_by_name: searchByName
      }, token);
      
      const data = response?.data?.data || response?.data || response;
      const result = data.results?.[0] || data;
      
      // Validate result is a proper object and not HTML/error response
      if (typeof result === 'string' || result instanceof String) {
        // Got a string response (likely HTML error page)
        setEligibilityResult({
          is_eligible: false,
          error: 'Server returned an invalid response. Please try again later.'
        });
        toast?.error('Server error - please try again');
        return;
      }
      
      // Check if result looks like an HTML error page
      if (result?.error && typeof result.error === 'string' && result.error.includes('<!DOCTYPE')) {
        setEligibilityResult({
          is_eligible: false,
          error: 'Connection timeout. The PerformCare portal may be slow or unavailable.'
        });
        toast?.error('Connection timeout - please try again');
        return;
      }
      
      setEligibilityResult(result);
      
      if (result?.cyber_id_discovered) {
        toast?.success(`CYBER ID discovered: ${result.cyber_id_discovered}`);
      } else if (result?.is_eligible) {
        toast?.success('Client is eligible');
      } else {
        toast?.info('Eligibility check complete');
      }
    } catch (error) {
      // Handle different error types
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      
      // Check if error contains HTML (Cloudflare error page)
      if (typeof errorMessage === 'string' && errorMessage.includes('<!DOCTYPE')) {
        setEligibilityResult({
          is_eligible: false,
          error: 'Connection timeout. The PerformCare portal may be slow or unavailable. Please try again.'
        });
        toast?.error('Connection timeout - please try again later');
      } else {
        setEligibilityResult({
          is_eligible: false,
          error: typeof errorMessage === 'string' ? errorMessage : 'Failed to verify eligibility'
        });
        toast?.error('Failed to verify eligibility');
      }
      console.error('Eligibility verification error:', error);
    } finally {
      setVerifyingEligibility(false);
    }
  };

  const startFullSync = async () => {
    setSyncing(true);
    setSyncJobId(null);
    setSyncJobStatus(null);
    
    try {
      const response = await apiCall('/performcare/live/historical/full-sync', 'POST', {
        include_inactive_clients: true,
        run_in_background: true
      }, token);
      
      const data = response?.data?.data || response?.data || response;
      
      if (data.job_id) {
        setSyncJobId(data.job_id);
        setSyncJobStatus(data);
        toast?.success('Full sync started in background');
        
        // Poll for status updates
        pollSyncStatus(data.job_id);
      } else {
        toast?.info('Sync completed');
        setSyncJobStatus(data);
      }
    } catch (error) {
      toast?.error('Failed to start sync');
      console.error(error);
      setSyncing(false);
    }
  };

  const pollSyncStatus = async (jobId) => {
    const checkStatus = async () => {
      try {
        const response = await apiCall(`/performcare/live/sync-job/${jobId}`, 'GET', null, token);
        const data = response?.data?.data || response?.data || response;
        setSyncJobStatus(data);
        
        if (data.status === 'running' || data.status === 'queued') {
          setTimeout(checkStatus, 5000); // Check every 5 seconds
        } else {
          setSyncing(false);
          if (data.status === 'completed') {
            toast?.success(`Sync completed: ${data.results?.items_processed || 0} items processed`);
          } else if (data.status === 'failed') {
            toast?.error('Sync failed');
          }
        }
      } catch (error) {
        console.error('Error polling sync status:', error);
        setSyncing(false);
      }
    };
    
    checkStatus();
  };

  const filteredClients = clients.filter(c => 
    c.external_ids?.cyber_id || c.medicaid_number
  );

  const showConnection = !showSections || showSections.includes('connection');
  const showEligibility = !showSections || showSections.includes('eligibility');
  const showSync = !showSections || showSections.includes('sync');

  return (
    <div className="space-y-6">
      {/* Status Card */}
      {showConnection && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Link className="w-5 h-5 text-blue-600" />
                Live Portal Connection
              </CardTitle>
              <CardDescription>
                Direct connection to PerformCare CYBER portal using web automation
              </CardDescription>
            </div>
            <Button 
              onClick={testConnection} 
              disabled={testing}
              variant="outline"
            >
              {testing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Test Connection
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <Label className="text-gray-600">Portal URL</Label>
              <p className="font-mono text-sm mt-1 truncate">
                {liveStatus?.portal_url || 'https://apps.performcarenj.org/CyberAng/PROD/CYBER'}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <Label className="text-gray-600">Credentials</Label>
              <div className="font-semibold mt-1">
                {liveStatus?.configured || liveStatus?.credentials_configured ? (
                  <Badge className="bg-green-100 text-green-800">Configured</Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-800">Not Configured</Badge>
                )}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <Label className="text-gray-600">Username</Label>
              <p className="font-mono text-sm mt-1">{liveStatus?.username || 'N/A'}</p>
            </div>
          </div>
          
          {liveStatus?.features && (
            <div className="mt-4">
              <Label className="text-gray-600">Available Features</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(liveStatus.features).map(([feature, enabled]) => (
                  <Badge 
                    key={feature} 
                    className={enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}
                  >
                    {feature.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Eligibility Verification */}
      {showEligibility && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            Live Eligibility Verification
          </CardTitle>
          <CardDescription>
            Verify client eligibility directly from PerformCare portal. 
            Use Name/DOB search to auto-discover CYBER IDs for new clients.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search Method Toggle */}
            <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchByName}
                  onChange={(e) => setSearchByName(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="font-medium">Search by Name/DOB</span>
              </label>
              <span className="text-sm text-gray-600">
                {searchByName 
                  ? "Will search by client name and date of birth to discover CYBER ID"
                  : "Will search by existing CYBER ID or Medicaid ID"
                }
              </span>
            </div>

            {/* Client Selection */}
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>Select Client</Label>
                <select 
                  className="w-full mt-1 p-2 border rounded-lg"
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                >
                  <option value="">Choose a client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.first_name} {client.last_name} 
                      {client.external_ids?.cyber_id 
                        ? ` (CYBER: ${client.external_ids.cyber_id})`
                        : ' (No CYBER ID)'
                      }
                    </option>
                  ))}
                </select>
              </div>
              <Button 
                onClick={verifyClientEligibility}
                disabled={verifyingEligibility || !selectedClient}
              >
                {verifyingEligibility ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                {searchByName ? 'Search by Name' : 'Verify Eligibility'}
              </Button>
            </div>
          </div>

          {eligibilityResult && typeof eligibilityResult === 'object' && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Verification Result</h4>
              
              {/* Show error state prominently if there's an error */}
              {eligibilityResult.error && !eligibilityResult.error.includes('<!') && (
                <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                  <AlertTriangle className="w-4 h-4 inline mr-2 text-yellow-600" />
                  <span className="text-yellow-800">{eligibilityResult.error}</span>
                </div>
              )}
              
              {/* Show CYBER ID Discovery Banner */}
              {eligibilityResult.cyber_id_discovered && (
                <div className="mb-4 p-3 bg-green-50 rounded border border-green-200">
                  <CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />
                  <span className="text-green-800 font-medium">
                    CYBER ID Discovered and Saved: {eligibilityResult.cyber_id_discovered}
                  </span>
                </div>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-gray-600">Status</Label>
                  <div className="font-semibold">
                    {eligibilityResult.is_eligible ? (
                      <Badge className="bg-green-100 text-green-800">Eligible</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800">Not Eligible</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-gray-600">CYBER ID</Label>
                  <div className="font-mono text-sm">
                    {eligibilityResult.cyber_id || 'N/A'}
                    {eligibilityResult.cyber_id_discovered && (
                      <Badge className="ml-2 bg-blue-100 text-blue-800 text-xs">NEW</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-gray-600">Plan</Label>
                  <p>{eligibilityResult.plan_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600">CMO</Label>
                  <p>{eligibilityResult.care_management_org || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Full Historical Sync */}
      {showSync && (
      <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-teal-600" />
                Full Historical Data Sync
              </CardTitle>
              <CardDescription>
                Pull all historical data from PerformCare including eligibility, authorizations, and service history
              </CardDescription>
            </div>
            <Button 
              onClick={startFullSync}
              disabled={syncing}
              variant="default"
            >
              {syncing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {syncing ? 'Syncing...' : 'Start Full Sync'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {syncJobStatus && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge className={
                  syncJobStatus.status === 'completed' ? 'bg-green-100 text-green-800' :
                  syncJobStatus.status === 'running' ? 'bg-blue-100 text-blue-800' :
                  syncJobStatus.status === 'failed' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }>
                  {syncJobStatus.status?.toUpperCase()}
                </Badge>
                {syncJobId && (
                  <span className="text-sm text-gray-500">Job ID: {syncJobId}</span>
                )}
              </div>

              {syncJobStatus.results && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-gray-800">{syncJobStatus.results.items_processed || 0}</p>
                    <p className="text-sm text-gray-600">Processed</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-800">{syncJobStatus.results.items_created || 0}</p>
                    <p className="text-sm text-green-600">Created</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-800">{syncJobStatus.results.items_updated || 0}</p>
                    <p className="text-sm text-blue-600">Updated</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-yellow-800">{syncJobStatus.results.items_skipped || 0}</p>
                    <p className="text-sm text-yellow-600">Skipped</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-800">{syncJobStatus.results.items_failed || 0}</p>
                    <p className="text-sm text-red-600">Failed</p>
                  </div>
                </div>
              )}

              {syncJobStatus.errors && syncJobStatus.errors.length > 0 && (
                <div className="mt-4">
                  <Label className="text-red-600">Errors ({syncJobStatus.errors.length})</Label>
                  <div className="mt-2 max-h-40 overflow-auto">
                    {syncJobStatus.errors.slice(0, 5).map((error, i) => (
                      <div key={i} className="p-2 bg-red-50 rounded mb-2 text-sm">
                        {error.client_id && <span className="font-mono">{error.client_id}: </span>}
                        {error.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!syncJobStatus && (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Click &quot;Start Full Sync&quot; to pull all historical data from PerformCare</p>
              <p className="text-sm mt-2">This will verify eligibility and import authorizations for all clients</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Sync Jobs */}
      {liveStatus?.recent_sync_jobs && liveStatus.recent_sync_jobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Recent Sync Jobs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {liveStatus.recent_sync_jobs.map((job, i) => (
                <div key={i} className="p-3 border rounded-lg flex justify-between items-center">
                  <div>
                    <Badge className="mr-2">{job.sync_type}</Badge>
                    <span className="text-sm text-gray-600">
                      {new Date(job.started_at).toLocaleString()}
                    </span>
                  </div>
                  <Badge className={
                    job.status === 'completed' ? 'bg-green-100 text-green-800' :
                    job.status === 'failed' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }>
                    {job.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </div>
  );
};


export default LivePortalTab;
